import { useLazyMemo, usePureEffect } from "@buzzeasy/shared-frontend-utilities";
import { SignalRHubV2 } from "@buzzeasy/shared-frontend-utilities/signalr";
import { HubConnectionState } from "@microsoft/signalr";
import { PropsWithChildren, ReactElement, useCallback, useContext, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { v4 as uuidV4 } from "uuid";
import { AgentStateContext } from "../../../components/providers/AgentStateContext";
import environmentConfig from "../../../environmentConfig";
import { useGetMyTeamsQuery } from "../../../redux/apis/teamsApi";
import { selectAuthInfo } from "../../../redux/authSlice";
import { RawAgentLiveData, RawAgentTodayData, RawWorkItemData } from "../../models/reports.agents";
import { RawQueueLiveData, RawQueueTodayData } from "../../models/reports.queues";
import { SignalRConnectionContext, SignalRConnectionContextValue, SignalRConnectionHandlers } from "./SignalRConnectionContext";
import { LiveHub, LiveHubClient, LiveHubSubscribeDto, TodayHub, TodayHubClient, TodayHubSubscribeDto } from "./SignalRConnectionContext.types";

const { apiBaseUrl } = environmentConfig;

export default function SignalRConnectionProvider({ children }: PropsWithChildren): ReactElement {
  const { token, user: { id: userId } } = useSelector(selectAuthInfo);
  const [{ businessUnits }] = useContext(AgentStateContext);

  const { data: foundTeams, isLoading } = useGetMyTeamsQuery(undefined, { pollingInterval: 60000 });

  const teams = useLazyMemo(
    () => foundTeams ?? [],
    [foundTeams],
  );

  const queueIds = useMemo(
    () => Array.from(new Set(teams.flatMap(t => t.queueIds))),
    [teams],
  );

  // #region Consumers management
  const [consumers, setConsumers] = useState<Record<string, Partial<SignalRConnectionHandlers>>>({});

  // consumersRef used not to tear down and rebuild SignalR connection every time on consumer change
  const consumersRef = useRef(consumers);
  consumersRef.current = consumers;

  const subscribe = useCallback(
    (handlers: Partial<SignalRConnectionHandlers>) => {
      const consumerId = uuidV4();
      setConsumers(curr => ({ ...curr, [consumerId]: handlers }));
      return consumerId;
    },
    [],
  );

  const unsubscribe = useCallback(
    (consumerId: string) => setConsumers((curr) => {
      const newConsumers = { ...curr };
      delete newConsumers[consumerId];
      return newConsumers;
    }),
    [],
  );
  // #endregion

  // #region SignalR event handlers
  const receiveTodaysQueueData = useCallback(
    (data: RawQueueTodayData) => Object.values(consumersRef.current).forEach(handlers => handlers.receiveTodaysQueueData?.(data)),
    [],
  );

  const receiveLiveQueueData = useCallback(
    (data: RawQueueLiveData) => Object.values(consumersRef.current).forEach(handlers => handlers.receiveLiveQueueData?.(data)),
    [],
  );

  const receiveTodaysAgentData = useCallback(
    (data: RawAgentTodayData) => Object.values(consumersRef.current).forEach(handlers => handlers.receiveTodaysAgentData?.(data)),
    [],
  );

  const receiveLiveAgentData = useCallback(
    (data: RawAgentLiveData[]) => Object.values(consumersRef.current).forEach(handlers => handlers.receiveLiveAgentData?.(data)),
    [],
  );

  const receiveWorkItemData = useCallback(
    (data: RawWorkItemData[]) => Object.values(consumersRef.current).forEach(handlers => handlers.receiveWorkItemData?.(data)),
    [],
  );
  // #endregion

  // #region TodayHub
  const todayHub = useMemo(
    () => {
      const hub = SignalRHubV2.create<TodayHub, TodayHubClient>(
        {
          baseUrl: `${apiBaseUrl}/aggregations/today/hub`,
          queryParams: { useNewSubscriptionMethod: "true" },
        },
        token,
      );

      hub.on("ReceiveTodaysAgentReportData", receiveTodaysAgentData);
      hub.on("ReceiveTodaysQueueData", receiveTodaysQueueData);

      return hub;
    },
    [receiveTodaysAgentData, receiveTodaysQueueData, token],
  );

  const todayHubParams = useMemo<TodayHubSubscribeDto>(
    () => ({
      queueIds,
      agentIds: [userId],
      businessUnitIds: businessUnits.map(bu => bu.id),
      includeDefaultBusinessUnit: true, // always include in order to not miss any data
    }),
    [businessUnits, queueIds, userId],
  );

  usePureEffect(
    (refs) => {
      if (isLoading) return;

      todayHub.start()
        .then(() => todayHub.sendSubscribeAsync(refs.todayHubParams))
        .catch(() => { /* do nothing */ });

      return () => { void todayHub.stop(); };
    },
    [isLoading, todayHub],
    { todayHubParams },
  );

  usePureEffect(
    (refs) => {
      if (refs.todayHub.state === HubConnectionState.Connected)
        void refs.todayHub.sendSubscribeAsync(todayHubParams);
    },
    [todayHubParams],
    { todayHub },
  );
  // #endregion

  // #region LiveHub
  const liveHub = useMemo(
    () => {
      const hub = SignalRHubV2.create<LiveHub, LiveHubClient>(
        {
          baseUrl: `${apiBaseUrl}/live/hub`,
          queryParams: { useNewSubscriptionMethod: "true" },
        },
        token,
      );

      hub.on("ReceiveQueueData", receiveLiveQueueData);
      hub.on("ReceiveTenantAgentStates", receiveLiveAgentData);
      hub.on("ReceiveWorkItemsWithConversations", receiveWorkItemData);

      return hub;
    },
    [receiveLiveAgentData, receiveLiveQueueData, receiveWorkItemData, token],
  );

  const liveHubParams = useMemo<LiveHubSubscribeDto>(
    () => ({
      queueIds,
      businessUnitIds: businessUnits.map(bu => bu.id),
      includeDefaultBusinessUnit: true, // always include in order to not miss any data
    }),
    [businessUnits, queueIds],
  );

  usePureEffect(
    (refs) => {
      if (isLoading) return;

      void liveHub.start()
        .then(() => liveHub.sendSubscribeAsync(refs.liveHubParams))
        .catch(() => { /* do nothing */ });

      return () => { void liveHub.stop(); };
    },
    [isLoading, liveHub],
    { liveHubParams },
  );

  usePureEffect(
    (refs) => {
      if (refs.liveHub.state === HubConnectionState.Connected)
        void refs.liveHub.sendSubscribeAsync(liveHubParams);
    },
    [liveHubParams],
    { liveHub },
  );

  const contextValue = useMemo<SignalRConnectionContextValue>(
    () => ({ subscribe, unsubscribe, teams }),
    [subscribe, teams, unsubscribe],
  );

  return (
    <SignalRConnectionContext.Provider value={contextValue}>
      {children}
    </SignalRConnectionContext.Provider>
  );
}
